// ============================================================================
// PacketPdfPreview — controlled PDF rendering via react-pdf (fit-to-pane)
//
// Renders PDF pages ourselves with PDF.js so the preview is a clean white
// surface with NO native browser PDF toolbar. Defaults to FIT-PAGE inside the
// bounded preview pane; zoom and rotation are driven by the parent so the
// toolbar is shared with the image preview. Page navigation is PDF-specific
// and lives here.
//
// PHI/data notes:
//   - `url` is a transient blob: object URL owned by the parent drawer. This
//     component never fetches a signed URL, never persists it, never shows a
//     filename, and never exposes a Storage path.
// ============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { safeUserError } from '../../core/safeError.js';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, RotateCcw } from 'lucide-react';

// Vite-friendly worker configuration (react-pdf v9 / pdfjs-dist v4 ship an .mjs
// worker). `new URL(..., import.meta.url)` lets Vite emit a hashed, same-origin
// asset URL for the worker at build time.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// zoom: user multiplier applied on top of fit (1 = fit-to-pane). rotation: degrees.
const PacketPdfPreview = ({ url, zoom = 1, rotation = 0, onRetry }) => {
  const [numPages, setNumPages]   = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(null);
  const [pageDims, setPageDims]   = useState(null); // { w, h } unscaled
  const [fitScale, setFitScale]   = useState(1);

  const surfaceRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0 });

  // Reset paging when a different document is previewed.
  useEffect(() => {
    setNumPages(null);
    setPageNumber(1);
    setLoadError(null);
    setPageDims(null);
  }, [url]);

  // Compute fit scale from the bounded pane and the page's unscaled dimensions.
  const recomputeFit = useCallback(() => {
    const { w, h } = sizeRef.current;
    if (!w || !h || !pageDims || !pageDims.w || !pageDims.h) return;
    const rotated = (rotation % 180) !== 0;
    const pw = rotated ? pageDims.h : pageDims.w;
    const ph = rotated ? pageDims.w : pageDims.h;
    const pad = 32;
    const s = Math.min((w - pad) / pw, (h - pad) / ph);
    setFitScale(s > 0 ? s : 1);
  }, [pageDims, rotation]);

  // Observe the pane size so fit recomputes on sidebar collapse / window resize.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return undefined;
    const measure = () => {
      sizeRef.current = { w: el.clientWidth, h: el.clientHeight };
      recomputeFit();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeFit]);

  useEffect(() => { recomputeFit(); }, [recomputeFit]);

  const goPrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const goNext = () => setPageNumber((p) => Math.min(numPages || 1, p + 1));

  const scale = (fitScale || 1) * (zoom || 1);

  if (loadError) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700 max-w-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Could not render this PDF</div>
              <div>{loadError}</div>
            </div>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-red-300 rounded-md text-red-700 hover:bg-red-100"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Scrollable page surface — clean light background, centered page */}
      <div ref={surfaceRef} className="flex-1 min-h-0 overflow-auto bg-slate-100 flex justify-center items-start p-4">
        <Document
          file={url}
          onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadError(null); }}
          onLoadError={(err) => {
            console.error('[PacketPdfPreview] PDF load error:', err);
            setLoadError(safeUserError(err));
          }}
          loading={
            <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Rendering PDF…
            </div>
          }
          error={
            <div className="text-sm text-red-600 py-10">Could not render this PDF.</div>
          }
        >
          <div className="shadow-md rounded overflow-hidden bg-white h-fit">
            <Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              onLoadSuccess={(page) => {
                const w = page.originalWidth || page.width;
                const h = page.originalHeight || page.height;
                if (w && h) setPageDims({ w, h });
              }}
              renderTextLayer
              renderAnnotationLayer
              loading={
                <div className="flex items-center gap-2 text-sm text-slate-500 p-10">
                  <Loader2 className="w-4 h-4 animate-spin" /> Rendering page…
                </div>
              }
            />
          </div>
        </Document>
      </div>

      {/* Page navigation (PDF-specific) */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-2 px-3 py-1.5 border-t border-slate-200 bg-white text-xs text-slate-600">
          <button
            type="button"
            onClick={goPrev}
            disabled={pageNumber <= 1}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="tabular-nums">Page {pageNumber} of {numPages}</span>
          <button
            type="button"
            onClick={goNext}
            disabled={pageNumber >= numPages}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default PacketPdfPreview;
